import { Fragment } from 'react'
import { Badge, Box, Icon, Link, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { Link as RouterLink } from 'react-router-dom'
import { FiGrid, FiHome, FiUsers, FiMail, FiKey, FiSliders } from 'react-icons/fi'
import {
  CORE_NAV,
  isPrefixActive,
  resolveActiveModule,
  visibleModules,
} from '../../modules/registry'
import { useProductTier } from '../../hooks/useClientConfig'
import { useAuth } from '../../hooks/useAuth'

interface NavItemProps {
  icon: React.ElementType
  label: string
  to: string
  isActive?: boolean
  isSubItem?: boolean
  collapsed?: boolean
}

function NavItem({ icon, label, to, isActive, isSubItem, collapsed }: NavItemProps) {
  const link = (
    <Link
      display="flex"
      alignItems="center"
      justifyContent={collapsed ? 'center' : 'flex-start'}
      gap={collapsed ? 0 : isSubItem ? 2 : 3}
      px={collapsed ? 2 : 4}
      pl={collapsed ? 2 : isSubItem ? 8 : 4}
      py={isSubItem ? 1.5 : 2}
      borderRadius="md"
      bg={isActive ? 'brand.50' : 'transparent'}
      color={isActive ? 'brand.600' : 'gray.600'}
      fontWeight={isActive ? 'medium' : 'normal'}
      fontSize={isSubItem ? 'sm' : 'md'}
      _hover={{ bg: isActive ? 'brand.50' : 'gray.100', textDecoration: 'none' }}
      _touch={{ minH: '2.75rem' }}
      transition="all 0.2s"
      asChild><RouterLink to={to}>
        <Icon as={icon} boxSize={isSubItem ? 4 : 5} flexShrink={0} />
        {!collapsed && <Text>{label}</Text>}
      </RouterLink></Link>
  )

  if (collapsed) {
    return (
      <Tooltip content={label} showArrow positioning={{
        placement: "right"
      }}>
        {link}
      </Tooltip>
    );
  }

  return link
}

function NavGroupHeader({
  label,
  icon,
  to,
  isActive,
  collapsed,
  badge,
}: {
  label: string
  icon?: React.ElementType
  to?: string
  isActive?: boolean
  collapsed?: boolean
  badge?: string
}) {
  if (to && icon) {
    const link = (
      <Link
        display="flex"
        alignItems="center"
        justifyContent={collapsed ? 'center' : 'flex-start'}
        gap={collapsed ? 0 : 3}
        mt={4}
        mb={1}
        px={collapsed ? 2 : 4}
        py={2}
        borderRadius="md"
        bg={isActive ? 'brand.50' : 'transparent'}
        color={isActive ? 'brand.600' : 'gray.600'}
        fontWeight="medium"
        _hover={{
          bg: isActive ? 'brand.50' : 'gray.100',
          textDecoration: 'none',
        }}
        _touch={{ minH: '2.75rem' }}
        transition="all 0.2s"
        minW={0}
        asChild><RouterLink to={to}>
          <Icon as={icon} boxSize={5} flexShrink={0} />
          {!collapsed && <Text truncate>{label}</Text>}
          {!collapsed && badge && (
            <Badge colorPalette="purple" fontSize="0.6rem" flexShrink={0}>
              {badge}
            </Badge>
          )}
        </RouterLink></Link>
    )

    if (collapsed) {
      return (
        <Tooltip content={label} showArrow positioning={{
          placement: "right"
        }}>
          {link}
        </Tooltip>
      );
    }

    return link
  }

  // Section labels are hidden in collapsed mode
  if (collapsed) return <Box mt={4} mb={1} />

  return (
    <Box mt={4} mb={1} px={4}>
      <Text
        fontSize="xs"
        fontWeight="bold"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {label}
      </Text>
    </Box>
  )
}

interface ModuleNavProps {
  pathname: string
  collapsed: boolean
  isAdmin: boolean
}

export function ModuleNav({ pathname, collapsed, isAdmin }: ModuleNavProps) {
  const activeModule = resolveActiveModule(pathname)
  const tier = useProductTier()
  // Scope Config is SuperAdmin-only (narrower than the rest of the Admin section, which
  // isAdmin already gates for Admin+SuperAdmin) -- read the role directly rather than
  // threading a second prop through both DashboardLayout and TabletDashboardShell.
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'SuperAdmin'

  return (
    <VStack gap={0} align="stretch" flex={1} overflowY="auto">
      <NavItem
        icon={FiHome}
        label="Dashboard"
        to="/dashboard"
        isActive={pathname === '/dashboard'}
        collapsed={collapsed}
      />

      {/* Shared core — always visible */}
      <NavGroupHeader label="Contacts" collapsed={collapsed} />
      {CORE_NAV.map((item) => (
        <NavItem
          key={item.to}
          icon={item.icon}
          label={item.label}
          to={item.to}
          isActive={isPrefixActive(pathname, item.activePrefixes)}
          isSubItem
          collapsed={collapsed}
        />
      ))}

      {/* Modules — the active one expands its sub-items */}
      <NavGroupHeader label="Modules" collapsed={collapsed} />
      {visibleModules(tier).map((mod) => (
        <Fragment key={mod.id}>
          <NavGroupHeader
            label={mod.label}
            icon={mod.icon}
            to={mod.basePath}
            data-active={activeModule?.id === mod.id}
            collapsed={collapsed}
            badge={mod.status === 'coming-soon' ? 'Soon' : undefined}
          />
          {activeModule?.id === mod.id &&
            mod.navItems.map((item) => (
              <NavItem
                key={item.to}
                icon={item.icon}
                label={item.label}
                to={item.to}
                isActive={isPrefixActive(pathname, item.activePrefixes)}
                isSubItem
                collapsed={collapsed}
              />
            ))}
        </Fragment>
      ))}

      {/* Admin section - visible only to admins */}
      {isAdmin && (
        <>
          <NavGroupHeader label="Admin" collapsed={collapsed} />
          <NavItem
            icon={FiUsers}
            label="Users"
            to="/dashboard/admin/users"
            isActive={isPrefixActive(pathname, ['/dashboard/admin/users'])}
            isSubItem
            collapsed={collapsed}
          />
          <NavItem
            icon={FiKey}
            label="Access Grants"
            to="/dashboard/admin/grants"
            isActive={isPrefixActive(pathname, ['/dashboard/admin/grants'])}
            isSubItem
            collapsed={collapsed}
          />
          <NavItem
            icon={FiMail}
            label="Invites"
            to="/dashboard/admin/invites"
            isActive={isPrefixActive(pathname, ['/dashboard/admin/invites'])}
            isSubItem
            collapsed={collapsed}
          />
          <NavItem
            icon={FiGrid}
            label="Committees"
            to="/dashboard/admin/committees"
            isActive={isPrefixActive(pathname, ['/dashboard/admin/committees'])}
            isSubItem
            collapsed={collapsed}
          />
          {isSuperAdmin && (
            <NavItem
              icon={FiSliders}
              label="Scope Config"
              to="/dashboard/admin/scope-config"
              isActive={isPrefixActive(pathname, ['/dashboard/admin/scope-config'])}
              isSubItem
              collapsed={collapsed}
            />
          )}
        </>
      )}
    </VStack>
  );
}
